import { PropsWithChildren, useEffect, useMemo } from 'react';

import {
  AbilityContext,
  defineAbilityFor,
} from '@cast/shared-ability-data-access';

import { setAbilityState } from './ability.state';
import { useUser } from '@cast/infrastructure-auth';
import { useOrganizationSettingsQuery } from './queries/useOrganizationSettingsQuery';
import { useFlagsQuery } from './queries/useFlagsQuery';
import { useOrganizations } from '@cast/shared-organization-context-data-access';

import { useRulesWithOverrides } from './useRulesWithOverrides';

export const AbilityProvider = ({ children }: PropsWithChildren<unknown>) => {
  const { isLoading: isLoadingFeatureFlags, data: featureFlags } =
    useFlagsQuery({ flagNamePrefix: 'ui_' });
  const user = useUser();
  const { currentOrganization, enterpriseOrgId } = useOrganizations();
  const { isLoading: isLoadingOrgSettings, data: orgSettings } =
    useOrganizationSettingsQuery({
      organizationId: currentOrganization?.id,
    });
  const { isLoading, rulesOrig, rules } = useRulesWithOverrides(
    featureFlags ?? [],
    user?.guid,
    orgSettings
  );

  const computedAbility = useMemo(() => {
    return {
      isLoading: isLoading || isLoadingFeatureFlags || isLoadingOrgSettings,
      ...defineAbilityFor(rules || [], {
        subjectId: user?.guid,
        organizationId: currentOrganization?.id,
        enterpriseId: currentOrganization?.parentId ?? enterpriseOrgId,
      }),
    };
  }, [
    isLoading,
    isLoadingFeatureFlags,
    isLoadingOrgSettings,
    rules,
    user?.guid,
    currentOrganization?.id,
    currentOrganization?.parentId,
    enterpriseOrgId,
  ]);

  useEffect(() => {
    if (computedAbility && rulesOrig) {
      setAbilityState(computedAbility, rulesOrig);
    }
  }, [rulesOrig, computedAbility]);

  // Global spinner is shown until abilities are not computed
  return (
    <AbilityContext.Provider value={computedAbility}>
      {children}
    </AbilityContext.Provider>
  );
};
